import type { MouseEvent, ReactElement } from 'react'
import { Link } from 'react-router-dom'

import { PRODUCT_ICON } from '@/packages/explore/components/product-icons'
import { useLocalize } from '@/packages/explore/lib/localize'
import { opensInNewTab } from '@/packages/explore/lib/opensInNewTab'
import type { Product } from '@/packages/explore/types'

interface ProductRowProps {
  product: Product
  onNavigate?: (() => void) | undefined
  nodeId?: string
}

/**
 * Figma `product` in the list view (2267:290959) — a 52px row: an 8px pad, a
 * 36px icon tile and the label block beside it.
 *
 * Hovering fills the whole row and turns the tile white; the tile also drops
 * its elevation there, exactly as the hovered row in the frame shows.
 */
export function ProductRow({ product, onNavigate, nodeId }: ProductRowProps): ReactElement {
  const localize = useLocalize()
  const Glyph = product.icon ? PRODUCT_ICON[product.icon] : undefined

  const externalUrl = product.href ? localize(product.href) : null

  const innerContent = (
    <>
      <span
        data-name="button-md"
        className="hub-tile-row text-icon flex size-9 shrink-0 items-center justify-center rounded-sm p-2"
      >
        {Glyph && <Glyph className="size-5" aria-hidden="true" />}
      </span>

      <span
        data-name="button-md"
        className="hub-label-md text-btn-text flex min-w-px flex-1 items-center gap-2 rounded-xs px-3 py-2"
      >
        {localize(product.label)}
      </span>
    </>
  )

  if (externalUrl) {
    return (
      <a
        href={externalUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-node-id={nodeId}
        data-name="product"
        className="hover:bg-btn-clear-hover flex w-full items-start rounded-md p-2 transition-colors"
      >
        {innerContent}
      </a>
    )
  }

  const handleClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (!opensInNewTab(event)) onNavigate?.()
  }

  return (
    <Link
      to={product.to || '#'}
      onClick={handleClick}
      data-node-id={nodeId}
      data-name="product"
      className="hover:bg-btn-clear-hover flex w-full items-start rounded-md p-2 transition-colors"
    >
      {innerContent}
    </Link>
  )
}
